import {
  EPSILON,
  EXPORT_MARGIN_MM,
  EXPORT_MAX_DIMENSION_PX,
  EXPORT_MAX_PIXELS,
  EXPORT_PIXELS_PER_MM,
} from './constants.js';
import type { Bounds } from './wall-model.js';
import type { Mm } from './units.js';
import { fail } from './errors.js';

/** World-to-image transform for a fitted, wall-only export (clause 14.5). */
export interface ExportLayout {
  readonly leftWorld: Mm;
  readonly topWorld: Mm;
  /** Pixels per millimetre. */
  readonly scale: number;
  readonly width: number;
  readonly height: number;
}

export function worldToPixel(layout: ExportLayout, x: Mm, y: Mm): readonly [number, number] {
  return [(x - layout.leftWorld) * layout.scale, (layout.topWorld - y) * layout.scale];
}

export interface ExportSizing {
  readonly marginMm?: Mm;
  readonly pixelsPerMm?: number;
  readonly maxDimensionPx?: number;
  readonly maxPixels?: number;
}

/** Fit wall bounds plus a mortar margin into a bounded raster canvas. */
export function calculateExportLayout(bounds: Bounds, sizing: ExportSizing = {}): ExportLayout {
  const {
    marginMm = EXPORT_MARGIN_MM,
    pixelsPerMm = EXPORT_PIXELS_PER_MM,
    maxDimensionPx = EXPORT_MAX_DIMENSION_PX,
    maxPixels = EXPORT_MAX_PIXELS,
  } = sizing;

  const values = [bounds.minX, bounds.minY, bounds.maxX, bounds.maxY];
  if (!values.every(Number.isFinite) || bounds.maxX <= bounds.minX || bounds.maxY <= bounds.minY) {
    fail('Wall bounds are invalid');
  }
  if (marginMm < 0 || pixelsPerMm <= 0 || maxDimensionPx <= 0 || maxPixels <= 0) {
    fail('Export sizing values must be positive');
  }

  const worldWidth = bounds.maxX - bounds.minX + marginMm * 2;
  const worldHeight = bounds.maxY - bounds.minY + marginMm * 2;
  let scale = Math.min(
    pixelsPerMm,
    maxDimensionPx / worldWidth,
    maxDimensionPx / worldHeight,
    Math.sqrt(maxPixels / (worldWidth * worldHeight)),
  );

  const imageSize = (candidate: number): readonly [number, number] => [
    Math.min(maxDimensionPx, Math.max(1, Math.ceil(worldWidth * candidate - EPSILON))),
    Math.min(maxDimensionPx, Math.max(1, Math.ceil(worldHeight * candidate - EPSILON))),
  ];

  let [width, height] = imageSize(scale);
  if (width * height > maxPixels) {
    // Ceil-to-pixel rounding can put the analytical result just over the
    // budget. A short binary search retains the largest safe scale.
    let low = 0;
    let high = scale;
    for (let i = 0; i < 60; i++) {
      const candidate = (low + high) / 2;
      const [w, h] = imageSize(candidate);
      if (w * h <= maxPixels) low = candidate;
      else high = candidate;
    }
    scale = low;
    [width, height] = imageSize(scale);
  }

  return {
    leftWorld: bounds.minX - marginMm,
    topWorld: bounds.maxY + marginMm,
    scale,
    width,
    height,
  };
}
